<template>
   <Head v-if="organisationInfo?.name">
      <title>{{ $t("meta.landing.title", { organisation: organisationInfo.name }) }}</title>
      <meta name="description" :content="$t('meta.landing.content', { organisation: organisationInfo.name })" />
   </Head>

   <ClientOnly>
      <PageLoader :isLoading="isLoading" :loadErrors="loadErrors">
         <PageSections v-if="pageContent" :contentSections="pageContent.sections" />
      </PageLoader>
   </ClientOnly>
</template>

<script setup lang="ts">
import { inject } from "vue";
import { useRoute } from "vue-router";
import { Head } from "@unhead/vue/components";
import { useI18n } from "@/vue-i18n/vue-i18n";
import { useToastKey } from "@/plugins/useToast";
import ContentApi, { type PageContent } from "@/data/api-layer/contentApi";
import { useBaseContentStore } from "@/stores/baseContentStore";
import type { PageResp } from "@/data/api-layer/contentApi";
import { useApiRequest } from "@/composables/useApiRequest";

const useToast = inject(useToastKey);
const { t: $t } = useI18n();
const baseContentStore = useBaseContentStore();
const { isLoading, execRequest, loadErrors } = useApiRequest();

// data & requests
// -----------------------------------------
const $route = useRoute();
const pageContent = ref<PageContent>(null);

// lifecycle
// -----------------------------------------
onMounted(async () => {
   const response: PageResp = await execRequest(() => ContentApi.getLanding());
   if (!response) return;

   pageContent.value = response.content;

   // if the user has deleted their account, show a toast (redirected from account to /?account_deleted)
   if ($route.query.account_deleted !== undefined) {
      useToast({
         title: $t("account.account_delete.toast.title"),
         html: `
            <p class="mb-2>${$t("account.account_delete.toast.text")}</p>
            <p>${$t("account.account_delete.toast.text2")}</p>
         `,
         confirmButtonText: $t("account.account_delete.toast.button"),
      });
   }
});

// computed
// -----------------------------------------
const organisationInfo = computed(() => {
   if (!baseContentStore.content) return null;

   return baseContentStore.content.organisationInfo;
});
</script>

<style scoped></style>
